/**
 * This file is part of Discorch.
 * Copyright (C) 2025 Discorch Contributors.
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published
 * by the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. If not, see <https://www.gnu.org/licenses/>.
 */

import { Studio } from "@/components/studio/Studio";
import { ThemeToggle } from "@/components/ThemeToggle";
import { processDataPackage } from "@/lib/process-package";
import type { PackageStats, ProcessedChannel, ProcessingError } from "@/types";
import { CaretRight, Code, Lightning, Shield } from "phosphor-react";
import { useState } from "react";
import { useDropzone } from "react-dropzone";

type FlowStep = "choice" | "safe-flow" | "risky-flow" | "studio";
type RiskyStep = "warning" | "setup";

interface AppState {
  currentStep: FlowStep;
  channels: ProcessedChannel[];
  stats: PackageStats | null;
  loading: boolean;
  error: ProcessingError | null;
}

interface FlowViewProps {
  onBack: () => void;
  onUpload: (files: File[]) => void;
  getRootProps: () => Record<string, unknown>;
  getInputProps: () => Record<string, unknown>;
  isDragActive: boolean;
  loading: boolean;
  error: string | null;
}

interface SetupStep {
  title: string;
  description: string;
  actionLabel?: string;
  actionUrl?: string;
}

const PageContainer: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => (
  <div className="flex min-h-screen items-center justify-center overflow-hidden p-6 md:p-10">
    <div className="w-full max-w-2xl space-y-8 text-center">{children}</div>
  </div>
);

const IconHeader: React.FC<{
  icon: React.ReactNode;
  title: string;
  subtitle: string;
}> = ({ icon, title, subtitle }) => (
  <div className="space-y-4">
    {icon}
    <h2 className="font-lato text-4xl font-light tracking-wide text-slate-900 dark:text-slate-100">
      {title}
    </h2>
    <p className="font-lato text-lg text-slate-700 dark:text-slate-300">
      {subtitle}
    </p>
  </div>
);

const Card: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <div className="rounded-xl border border-slate-300 bg-slate-100 dark:border-slate-700 dark:bg-slate-900">
    {children}
  </div>
);

const CardSection: React.FC<{
  children: React.ReactNode;
  withBorder?: boolean;
}> = ({ children, withBorder = false }) => (
  <div
    className={
      withBorder ? "border-t border-slate-200 dark:border-slate-700" : ""
    }
  >
    <div className="p-8">{children}</div>
  </div>
);

const BackButton: React.FC<{ onClick: () => void; label?: string }> = ({
  onClick,
  label = "← Back to choice",
}) => (
  <button
    onClick={onClick}
    className="font-lato cursor-pointer text-slate-600 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-200"
  >
    {label}
  </button>
);

const DropZone: React.FC<{
  getRootProps: () => Record<string, unknown>;
  getInputProps: () => Record<string, unknown>;
  isDragActive: boolean;
  loading: boolean;
  error: string | null;
}> = ({ getRootProps, getInputProps, isDragActive, loading, error }) => (
  <>
    <div
      {...getRootProps()}
      className={`cursor-pointer rounded-lg border-2 border-dashed p-10 text-center transition-colors ${
        isDragActive
          ? "border-slate-600 bg-slate-100 dark:bg-slate-800/50"
          : "border-slate-400 bg-slate-100/50 hover:bg-slate-100 dark:border-slate-500 dark:bg-slate-800/20 dark:hover:bg-slate-800/40"
      }`}
    >
      <input {...getInputProps()} />
      <div className="flex min-h-[3rem] flex-col justify-center space-y-2">
        {loading ? (
          <p className="font-lato text-slate-700 dark:text-slate-300">
            Processing your data...
          </p>
        ) : isDragActive ? (
          <p className="font-lato text-slate-800 dark:text-slate-200">
            Drop your file here
          </p>
        ) : (
          <>
            <p className="font-lato text-slate-700 dark:text-slate-300">
              Drag your zip file here or click to select
            </p>
            <p className="font-lato text-xs text-slate-600 dark:text-slate-400">
              Supports Discord data package zip files
            </p>
          </>
        )}
      </div>
    </div>
    {error && (
      <p className="font-lato mt-3 text-sm text-red-600 dark:text-red-400">
        {error}
      </p>
    )}
  </>
);

const SetupStepItem: React.FC<{ step: SetupStep; stepNumber: number }> = ({
  step,
  stepNumber,
}) => (
  <div className="space-y-3">
    <h3 className="font-lato text-lg font-medium text-slate-900 dark:text-slate-100">
      Step {stepNumber}: {step.title}
    </h3>
    <p className="font-lato text-sm text-slate-700 dark:text-slate-300">
      {step.description}
    </p>
    {step.actionUrl && step.actionLabel && (
      <a
        href={step.actionUrl}
        target="_blank"
        rel="noopener noreferrer"
        className="font-lato inline-block cursor-pointer rounded-lg bg-blue-600 px-4 py-2 text-sm text-white transition-colors hover:bg-blue-700"
      >
        {step.actionLabel}
      </a>
    )}
  </div>
);

const initialState: AppState = {
  currentStep: "choice",
  channels: [],
  stats: null,
  loading: false,
  error: null,
};

export function App() {
  const [state, setState] = useState<AppState>(initialState);

  const handleDrop = async (acceptedFiles: File[]) => {
    if (acceptedFiles.length === 0) return;

    setState((prev) => ({ ...prev, loading: true, error: null }));

    try {
      const { channels, stats } = await processDataPackage(acceptedFiles[0]);
      setState((prev) => ({
        ...prev,
        channels,
        stats,
        currentStep: "studio",
        loading: false,
      }));
    } catch (err) {
      console.error(err);
      setState((prev) => ({
        ...prev,
        error: {
          type: "PARSE_ERROR",
          message:
            err instanceof Error
              ? err.message
              : "there was a problem processing your file, make sure it's a discord data package zip",
          details: err,
        },
        loading: false,
      }));
    }
  };

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleDrop,
    accept: {
      "application/zip": [".zip"],
    },
    maxFiles: 1,
    disabled: state.loading,
  });

  const handleSetCurrentStep = (step: FlowStep) => {
    setState((prev) => ({ ...prev, currentStep: step }));
  };

  const handleBackToChoice = () => {
    setState(initialState);
  };

  const handleSetCurrentScreen = (screen: "landing" | "studio") => {
    setState(
      screen === "landing"
        ? initialState
        : (prev) => ({ ...prev, currentStep: "studio" }),
    );
  };

  if (state.currentStep === "studio") {
    if (!state.stats) throw new Error("Stats not available for Studio view");

    return (
      <Studio
        channels={state.channels}
        stats={state.stats}
        setCurrentScreen={handleSetCurrentScreen}
      />
    );
  }

  return (
    <div className="font-lato min-h-screen overflow-hidden bg-slate-50 py-8 dark:bg-slate-950">
      {/* Theme Toggle */}
      <div className="absolute top-4 right-4 z-10">
        <ThemeToggle />
      </div>

      {state.currentStep === "choice" && (
        <ChoiceView onSetStep={handleSetCurrentStep} />
      )}

      {state.currentStep === "safe-flow" && (
        <SafeFlowView
          onBack={handleBackToChoice}
          onUpload={handleDrop}
          getRootProps={getRootProps}
          getInputProps={getInputProps}
          isDragActive={isDragActive}
          loading={state.loading}
          error={state.error?.message ?? null}
        />
      )}

      {state.currentStep === "risky-flow" && (
        <RiskyFlowView
          onBack={handleBackToChoice}
          onUpload={handleDrop}
          getRootProps={getRootProps}
          getInputProps={getInputProps}
          isDragActive={isDragActive}
          loading={state.loading}
          error={state.error?.message ?? null}
        />
      )}
    </div>
  );
}

const ChoiceView: React.FC<{ onSetStep: (step: FlowStep) => void }> = ({
  onSetStep,
}) => (
  <div className="flex min-h-screen items-center justify-center overflow-hidden p-6">
    <div className="w-full max-w-2xl space-y-8 text-center">
      <div className="space-y-3">
        <h1 className="text-4xl font-black tracking-tight text-slate-900 dark:text-slate-100">
          Discorch
        </h1>
        <p className="mx-auto max-w-lg text-base text-slate-700 dark:text-slate-300">
          Browse your Discord history and request message deletions
        </p>
      </div>

      {/* Privacy Banner */}
      <div className="rounded-xl border-2 border-blue-600 bg-blue-50 dark:border-blue-500 dark:bg-blue-950">
        <div className="p-6">
          <div className="space-y-2 text-sm leading-relaxed text-blue-800 dark:text-blue-200">
            <p>
              This tool works entirely offline and does not submit your data to
              any servers. It does not require you to log in with your Discord
              account.
            </p>
            <p>
              It merely helps you navigate your data package from Discord and
              craft data deletion requests to send to their privacy team. In
              cases where their privacy team's policies prohibit deletion, it
              guides you through automating the remaining tasks yourself.
            </p>
          </div>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        <button
          onClick={() => onSetStep("safe-flow")}
          className="group relative cursor-pointer overflow-hidden rounded-xl border-2 border-green-600 bg-green-50 text-left transition-colors hover:bg-green-100/70 dark:border-green-500 dark:bg-green-950 dark:hover:bg-green-900/70"
        >
          <div className="p-8">
            <div className="flex items-start justify-between">
              <div className="flex-1 space-y-4">
                <Shield
                  size={32}
                  className="text-green-700 dark:text-green-400"
                />
                <div className="space-y-3">
                  <h3 className="text-xl font-bold text-green-900 dark:text-green-100">
                    Play it safe
                  </h3>
                  <p className="text-sm leading-relaxed text-green-800 dark:text-green-200">
                    Follow Discord's terms. Only delete messages from servers
                    and group chats you can no longer access.
                  </p>
                </div>
              </div>
              <CaretRight
                size={20}
                className="mt-2 shrink-0 text-green-600 dark:text-green-500"
              />
            </div>
          </div>
        </button>

        <button
          onClick={() => onSetStep("risky-flow")}
          className="group relative cursor-pointer overflow-hidden rounded-xl border-2 border-red-600 bg-red-50 text-left transition-colors hover:bg-red-100/70 dark:border-red-500 dark:bg-red-950 dark:hover:bg-red-900/70"
        >
          <div className="p-8">
            <div className="flex items-start justify-between">
              <div className="flex-1 space-y-4">
                <Lightning
                  size={32}
                  className="text-red-700 dark:text-red-400"
                />
                <div className="space-y-3">
                  <h3 className="text-xl font-bold text-red-900 dark:text-red-100">
                    Live on the edge
                  </h3>
                  <p className="text-sm leading-relaxed text-red-800 dark:text-red-200">
                    Understand the risks. Automate deletion of 1:1 DMs against
                    Discord's terms.
                  </p>
                </div>
              </div>
              <CaretRight
                size={20}
                className="mt-2 shrink-0 text-red-600 dark:text-red-500"
              />
            </div>
          </div>
        </button>
      </div>

      {/* Information Section */}
      <div className="mt-6 space-y-4 text-left">
        <Card>
          <div className="p-6">
            <div className="space-y-2">
              <h3 className="font-bold text-slate-900 dark:text-slate-100">
                How this works
              </h3>
              <div className="space-y-4 text-sm leading-relaxed text-slate-700 dark:text-slate-300">
                <p>
                  Discord has an undocumented privacy request process that this
                  tool helps you navigate.
                </p>
                <p>
                  Discord acts in malicious compliance with the GDPR. Following
                  a surge of message deletion requests, their new policy states
                  that messages are considered invaluable to other users in
                  contexts you can still access (permission-wise on their
                  backend). You can never truly lose access to DMs, for example,
                  since you can always re-open them, so these cannot be deleted
                  through a GDPR privacy request. This was previously possible;
                  however, Discord's management felt the need to find a way to
                  make user mass exodus more difficult. On the other hand, the
                  moment you leave a server or group DM, the claim that messages
                  are invaluable to other users is somehow no longer valid,
                  according to Discord, and they'll happily delete them via a
                  privacy request.
                </p>
                <p>
                  It's completely backwards logic that only exists to make your
                  life difficult. That's why you get two options here: one for
                  the stuff Discord will actually delete, and another for 1:1
                  DMs where you need to get creative.
                </p>
              </div>
            </div>
          </div>

          <CardSection withBorder>
            <h4 className="mb-3 text-sm font-semibold text-slate-900 dark:text-slate-100">
              Getting your data package
            </h4>
            <div className="space-y-3 text-sm text-slate-700 dark:text-slate-300">
              <p>
                Both options require uploading your Discord data package. You
                only need to select "Messages" when exporting it.
              </p>
              <a
                href="https://support.discord.com/hc/en-us/articles/360004957991-Your-Discord-Data-Package"
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300"
              >
                <span>→ Discord data package export guide</span>
              </a>
            </div>
          </CardSection>

          <CardSection withBorder>
            <h4 className="mb-3 text-sm font-semibold text-slate-900 dark:text-slate-100">
              Privacy and security
            </h4>
            <div className="space-y-3 text-sm text-slate-700 dark:text-slate-300">
              <p>
                You can disable your internet connection to be extra sure we're
                not doing anything nefarious with your data package.
              </p>
              <p>The code is also fully open source for your verification.</p>
            </div>
          </CardSection>
        </Card>

        {/* Footer with Codeberg badge */}
        <div className="flex justify-center pt-3">
          <a
            href="https://codeberg.org/discorch/discorch"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex cursor-pointer items-center gap-2 rounded-xl border-2 border-slate-400 bg-slate-200 px-4 py-3 text-sm font-medium text-slate-800 transition-colors hover:bg-slate-300/70 dark:border-slate-600 dark:bg-slate-800 dark:text-slate-200 dark:hover:bg-slate-700/70"
          >
            <Code size={14} />
            <span>View source on Codeberg</span>
          </a>
        </div>
      </div>
    </div>
  </div>
);

const SafeFlowView: React.FC<FlowViewProps> = ({
  onBack,
  getRootProps,
  getInputProps,
  isDragActive,
  loading,
  error,
}) => (
  <PageContainer>
    <IconHeader
      icon={
        <Shield
          size={48}
          className="mx-auto text-slate-600 dark:text-slate-400"
        />
      }
      title="Safe and compliant"
      subtitle="This approach follows Discord's terms of service completely"
    />

    <Card>
      <div className="p-10">
        <div className="space-y-4">
          <h3 className="font-lato text-xl font-medium text-slate-900 dark:text-slate-100">
            What this will do
          </h3>
          <ul className="font-lato space-y-4 text-sm text-slate-700 dark:text-slate-300">
            <li className="flex items-center gap-3">
              <div className="h-1.5 w-1.5 shrink-0 rounded-full bg-slate-600" />
              <span>
                Create deletion requests for server messages you can no longer
                access
              </span>
            </li>
            <li className="flex items-center gap-3">
              <div className="h-1.5 w-1.5 shrink-0 rounded-full bg-slate-600" />
              <span>
                Create deletion requests for group DM messages you've left
              </span>
            </li>
            <li className="flex items-center gap-3">
              <div className="h-1.5 w-1.5 shrink-0 rounded-full bg-slate-500" />
              <span>
                1:1 DMs will remain untouched (Discord won't delete these
                anyway)
              </span>
            </li>
          </ul>
        </div>
      </div>

      <CardSection withBorder>
        <h4 className="font-lato mb-3 text-sm font-medium text-slate-900 dark:text-slate-100">
          Upload your Discord data package
        </h4>
        <DropZone
          getRootProps={getRootProps}
          getInputProps={getInputProps}
          isDragActive={isDragActive}
          loading={loading}
          error={error}
        />
      </CardSection>
    </Card>

    <BackButton onClick={onBack} />
  </PageContainer>
);

const RiskyFlowView: React.FC<FlowViewProps> = ({
  onBack,
  getRootProps,
  getInputProps,
  isDragActive,
  loading,
  error,
}) => {
  const [step, setStep] = useState<RiskyStep>("warning");

  const setupSteps: SetupStep[] = [
    {
      title: "Use Brave browser (recommended)",
      description:
        "Violentmonkey is no longer supported on Chrome due to its Manifest V2 architecture. As an alternative, consider using Brave browser which is also open source and Chromium-based.",
    },
    {
      title: "Install Violentmonkey",
      description: "This extension allows running the deletion scripts",
      actionLabel: "Install Violentmonkey",
      actionUrl:
        "https://chrome.google.com/webstore/detail/violentmonkey/jinjaccalgkegednnccohejagnlnfdag",
    },
    {
      title: "Install Vencord userscript",
      description: "This provides the foundation for Discord modifications",
      actionLabel: "Install Vencord",
      actionUrl:
        "https://raw.githubusercontent.com/Vencord/builds/main/Vencord.user.js",
    },
    {
      title: "Install DiscorchDeleter userscript",
      description: "This is the actual deletion automation tool",
      actionLabel: "Install DiscorchDeleter",
      actionUrl:
        "https://github.com/Darker-Ink/DiscorchDeleter/releases/download/v1.1.0/discorchdeleter.user.js",
    },
  ];

  if (step === "warning") {
    return (
      <PageContainer>
        <IconHeader
          icon={
            <Lightning
              size={48}
              className="mx-auto text-slate-600 dark:text-slate-400"
            />
          }
          title="Proceed with caution"
          subtitle="Automating 1:1 DM deletion violates Discord's terms of service"
        />

        <Card>
          <CardSection>
            <div className="space-y-2">
              <h3 className="font-bold text-slate-900 dark:text-slate-100">
                How this works
              </h3>
              <div className="space-y-4 text-sm leading-relaxed text-slate-700 dark:text-slate-300">
                <p>
                  This method hooks into Discord's web client and uses the exact
                  same deletion code that runs when you manually press delete on
                  a message. It respects Discord's rate limits and doesn't
                  access your authentication token directly.
                </p>
                <p>
                  While this violates Discord's terms of service, we haven't
                  observed any account restrictions in practice. This is
                  considered the safest automation method available, but use
                  your own judgement.
                </p>
                <p>
                  Requires installing browser extensions and userscripts. The
                  setup process is guided in the next steps.
                </p>
              </div>
            </div>
          </CardSection>

          <CardSection withBorder>
            <h4 className="mb-3 text-sm font-semibold text-slate-900 dark:text-slate-100">
              Important considerations
            </h4>
            <div className="space-y-3 text-sm text-slate-700 dark:text-slate-300">
              <p>
                This approach violates Discord's terms of service, though no
                account restrictions have been observed in practice.
              </p>
              <p>
                The method uses Discord's own client code rather than direct API
                access, making it the safest automation approach available.
              </p>
            </div>
          </CardSection>
        </Card>

        <div className="flex justify-center gap-6">
          <button
            onClick={onBack}
            className="font-lato cursor-pointer rounded-lg border-2 border-slate-300 px-8 py-3 text-slate-600 transition-colors hover:bg-slate-50 dark:border-slate-600 dark:text-slate-400 dark:hover:bg-slate-800"
          >
            Take me back
          </button>
          <button
            onClick={() => setStep("setup")}
            className="font-lato cursor-pointer rounded-lg bg-red-600 px-8 py-3 text-white transition-colors hover:bg-red-700"
          >
            I understand the risks
          </button>
        </div>
      </PageContainer>
    );
  }

  if (step === "setup") {
    return (
      <PageContainer>
        <IconHeader
          icon={
            <Lightning
              size={48}
              className="mx-auto text-slate-600 dark:text-slate-400"
            />
          }
          title="Setup required tools"
          subtitle="Follow these steps to enable automated DM deletion"
        />

        <Card>
          <CardSection>
            <div className="space-y-6">
              {setupSteps.map((stepItem, index) => (
                <SetupStepItem
                  key={index}
                  step={stepItem}
                  stepNumber={index + 1}
                />
              ))}

              {/* Step 5 - Upload */}
              <div className="space-y-3">
                <h3 className="font-lato text-lg font-medium text-slate-900 dark:text-slate-100">
                  Step 5: Upload your Discord data package
                </h3>
                <p className="font-lato text-sm text-slate-700 dark:text-slate-300">
                  Once all tools are installed, upload your data package to
                  begin
                </p>
                <DropZone
                  getRootProps={getRootProps}
                  getInputProps={getInputProps}
                  isDragActive={isDragActive}
                  loading={loading}
                  error={error}
                />
              </div>
            </div>
          </CardSection>
        </Card>

        <BackButton onClick={onBack} />
      </PageContainer>
    );
  }

  return null;
};
